import React from 'react';
import {
  Eye,
  Award,
  Info,
  ThumbsUp,
  ThumbsDown,
  SearchX,
  Users,
  Store,
  Tag,
  CircleDashed,
  TrendingUp,
} from 'lucide-react';

// An element is a Driver / Blocker when its positive vs negative mention share
// diverges by at least this much; otherwise it reads as a Mixed reaction.
const DELTA_THRESHOLD = 10;
const MAX_HEATMAP_ELEMENTS = 5;
// Cap the unified Key Elements list to the top-ranked elements (by total
// reaction share) so the report stays focused on what moved shoppers most.
const MAX_KEY_ELEMENTS = 5;

interface ElementReaction {
  theme: string;
  mention_percentage?: number;
  quotes: string[];
}

interface ElementAnalysis {
  name: string;
  positive_reactions: ElementReaction[];
  negative_reactions: ElementReaction[];
  key_insight: string;
}

interface DimensionScore {
  dimension: string;
  score: number | null; // null when the dimension has no elements (not evaluated)
  element_count: number;
}

interface BoundingBox {
  element_name: string;
  x_percent: number;
  y_percent: number;
  width_percent: number;
  height_percent: number;
}

interface CategoryGap {
  element: string;
  why_expected: string;
  competitor_signal?: string;
  audience_priority_signal?: string;
  category_norm_basis?: string;
}

interface ImprovementLever {
  action: string;
  rationale: string;
  lever: string;
  expected_impact: string;
}

interface ImprovementPlanData {
  headline: string;
  current_state: string;
  levers: ImprovementLever[];
}

interface FrontAnalysisData {
  overall_score: number;
  dimension_scores: DimensionScore[];
  purchase_intent_distribution: { wont_buy: number; neutral: number; will_buy: number };
  elements: ElementAnalysis[];
  bounding_boxes: BoundingBox[];
  raw_rationale_count: number;
  benchmark_will_buy?: number;
  percentile_rank?: number;
  category_gaps?: CategoryGap[];
  improvement_plan?: ImprovementPlanData | null;
}

interface FrontAnalysisSectionProps {
  data: FrontAnalysisData;
  frontImageUrl?: string;
  buildImageUrl: (url: string) => string;
}

function getTopMention(el: ElementAnalysis, type: 'positive' | 'negative'): number {
  const reactions = type === 'positive' ? el.positive_reactions : el.negative_reactions;
  return Math.max(0, ...reactions.map((r) => r.mention_percentage || 0));
}

type ElementRole = 'driver' | 'blocker' | 'mixed';

// Classify an element by how far its liked vs disliked mention share diverges.
// Net-positive beyond the threshold → Driver; net-negative → Blocker; close → Mixed.
function classifyElement(el: ElementAnalysis): ElementRole {
  const pos = getTopMention(el, 'positive');
  const neg = getTopMention(el, 'negative');
  if (pos - neg >= DELTA_THRESHOLD) return 'driver';
  if (neg - pos >= DELTA_THRESHOLD) return 'blocker';
  return 'mixed';
}

const ROLE_STYLES: Record<ElementRole, { label: string; badge: string }> = {
  driver: { label: 'Driver', badge: 'text-emerald-700 bg-emerald-100' },
  blocker: { label: 'Blocker', badge: 'text-red-700 bg-red-100' },
  mixed: { label: 'Mixed', badge: 'text-amber-700 bg-amber-100' },
};

const ROLE_BORDER: Record<ElementRole, string> = {
  driver: 'border-emerald-200 bg-emerald-50/30',
  blocker: 'border-red-200 bg-red-50/30',
  mixed: 'border-amber-200 bg-amber-50/30',
};

const HeroScore: React.FC<{
  score: number;
  benchmark_will_buy?: number;
}> = ({ score, benchmark_will_buy }) => {
  const delta = benchmark_will_buy !== undefined ? score - benchmark_will_buy : 0;
  const isAboveNorm = delta >= 0;
  const pct = Math.min(100, Math.max(0, score));
  const radius = 54;
  const stroke = 8;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference * (1 - pct / 100);

  const ringColor =
    score >= 70 ? '#10B981' : score >= 50 ? '#F97316' : score >= 30 ? '#F59E0B' : '#EF4444';
  const trackColor = '#F1F5F9';

  return (
    <div className="flex flex-col items-center py-6">
      <p className="text-sm font-semibold text-[#1C1C1E] mb-3">Front Performance Score</p>
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={radius} fill="none" stroke={trackColor} strokeWidth={stroke} />
          <circle
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashoffset}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-[#1C1C1E]">{Math.round(score)}</span>
          <span className="text-[10px] text-[#9CA3AF] -mt-0.5">/ 100</span>
        </div>
      </div>

      {benchmark_will_buy !== undefined && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-[#6B7280] flex items-center gap-1">
            Norm: {benchmark_will_buy.toFixed(0)}
            <span className="relative group/info">
              <Info className="w-3 h-3 text-[#9CA3AF] cursor-help" />
              <span className="hidden group-hover/info:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#1C1C1E] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-50">
                Average baseline front performance score across CPG categories
              </span>
            </span>
          </span>
          <span
            className={`text-xs font-semibold px-1.5 py-0.5 rounded ${isAboveNorm ? 'text-emerald-700 bg-emerald-50' : 'text-orange-700 bg-orange-50'}`}
          >
            {isAboveNorm ? '+' : ''}
            {delta.toFixed(0)} points
          </span>
        </div>
      )}
    </div>
  );
};

const AttentionHeatmap: React.FC<{
  imageUrl: string;
  boxes: BoundingBox[];
  elements: ElementAnalysis[];
  buildImageUrl: (url: string) => string;
}> = ({ imageUrl, boxes, elements, buildImageUrl }) => {
  if (!boxes.length) return null;

  const elementMap = new Map(elements.map((el) => [el.name, el]));

  // Normalize pixel values to percentages if LLM returned pixels
  const maxVal = Math.max(...boxes.map((b) => b.x_percent + b.width_percent));
  const normalize = maxVal > 100;
  const scale = normalize ? 100 / maxVal : 1;

  const boxData = boxes
    .filter((b) => b.width_percent > 0 || b.height_percent > 0)
    .map((b) => {
      const nb = normalize
        ? {
            ...b,
            x_percent: b.x_percent * scale,
            y_percent: b.y_percent * scale,
            width_percent: b.width_percent * scale,
            height_percent: b.height_percent * scale,
          }
        : b;
      return nb;
    })
    .map((b) => {
      const el = elementMap.get(b.element_name);
      const posPct = el
        ? Math.max(0, ...el.positive_reactions.map((r) => r.mention_percentage || 0))
        : 0;
      const negPct = el
        ? Math.max(0, ...el.negative_reactions.map((r) => r.mention_percentage || 0))
        : 0;
      const topMention = Math.min(100, posPct + negPct);
      return { ...b, topMention, el };
    })
    .sort((a, b) => b.topMention - a.topMention)
    .slice(0, MAX_HEATMAP_ELEMENTS);

  const boxColors = ['#07E5D1', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Image with outlined boxes */}
        <div className="relative inline-block w-full print-keep-overflow">
          <img
            src={buildImageUrl(imageUrl)}
            alt="Front packaging with element annotations"
            className="w-full h-auto rounded-lg"
          />
          {[...boxData]
            .sort((a, b) => b.width_percent * b.height_percent - a.width_percent * a.height_percent)
            .map((box) => {
              const rank = boxData.indexOf(box);
              const color = boxColors[rank % boxColors.length];
              return (
                <div
                  key={`box-${rank}`}
                  className="absolute rounded"
                  style={{
                    left: `${box.x_percent}%`,
                    top: `${box.y_percent}%`,
                    width: `${box.width_percent}%`,
                    bottom: `${100 - box.y_percent - box.height_percent}%`,
                    border: `2px solid ${color}`,
                    zIndex: rank + 1,
                  }}
                >
                  <span
                    className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: color }}
                  >
                    {rank + 1}
                  </span>
                </div>
              );
            })}
        </div>

        {/* Ranked list */}
        <div className="space-y-2 flex flex-col justify-center">
          {boxData.map((box, idx) => {
            const color = boxColors[idx % boxColors.length];
            return (
              <div key={idx} className="flex items-start gap-2.5">
                <span
                  className="flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: color }}
                >
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[#1C1C1E] font-semibold">{box.element_name}</span>
                    {box.el &&
                      (() => {
                        const role = classifyElement(box.el);
                        return (
                          <span
                            className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${ROLE_STYLES[role].badge}`}
                          >
                            {ROLE_STYLES[role].label}
                          </span>
                        );
                      })()}
                    <span className="text-[10px] text-[#6B7280]">{box.topMention}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 print-bar">
                    <div
                      className="h-full rounded-full print-bar"
                      style={{ width: `${Math.max(5, box.topMention)}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// One quote line: theme + first verbatim quote, colored by sentiment.
const ReactionQuote: React.FC<{ reaction: ElementReaction; positive: boolean }> = ({
  reaction,
  positive,
}) => (
  <div className="mb-1.5">
    <p className={`text-xs font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
      {reaction.theme}
    </p>
    {reaction.quotes.slice(0, 1).map((q, qIdx) => (
      <p
        key={qIdx}
        className={`text-xs text-[#6B7280] italic mt-0.5 pl-3 border-l-2 ${positive ? 'border-emerald-200' : 'border-red-200'}`}
      >
        &ldquo;{q}&rdquo;
      </p>
    ))}
  </div>
);

// Unified ranked list of every element, tagged Driver / Blocker / Mixed, always
// showing both what shoppers liked and disliked. Replaces the old separate
// Top Drivers / Top Blockers / Mixed Reactions cards.
const KeyElementsList: React.FC<{ elements: ElementAnalysis[] }> = ({ elements }) => {
  // Rank by total reaction share (liked + disliked) — how much of the audience
  // this element moved, regardless of direction.
  const ranked = [...elements]
    .filter((el) => el.positive_reactions.length > 0 || el.negative_reactions.length > 0)
    .sort(
      (a, b) =>
        getTopMention(b, 'positive') +
        getTopMention(b, 'negative') -
        (getTopMention(a, 'positive') + getTopMention(a, 'negative')),
    )
    .slice(0, MAX_KEY_ELEMENTS);

  if (!ranked.length) return null;

  return (
    <div>
      <div className="space-y-3">
        {ranked.map((el, idx) => {
          const role = classifyElement(el);
          const pos = getTopMention(el, 'positive');
          const neg = getTopMention(el, 'negative');
          const total = Math.min(100, pos + neg);
          return (
            <div key={idx} className={`border rounded-lg p-4 ${ROLE_BORDER[role]}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1C1C1E] text-white text-[10px] font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <h4 className="font-semibold text-sm text-[#1C1C1E]">{el.name}</h4>
                <span
                  className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${ROLE_STYLES[role].badge}`}
                >
                  {ROLE_STYLES[role].label}
                </span>
                <span className="text-[10px] text-[#6B7280] ml-auto whitespace-nowrap">
                  {total}% reacted
                </span>
              </div>

              {/* Split liked/disliked share bar */}
              <div className="w-full h-1.5 bg-gray-100 rounded-full mb-3 flex overflow-hidden print-bar print-keep-overflow">
                <div className="h-full bg-emerald-500 print-bar" style={{ width: `${pos}%` }} />
                <div className="h-full bg-red-500 print-bar" style={{ width: `${neg}%` }} />
              </div>

              {/* Always show both what people liked and disliked */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <div>
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <ThumbsUp className="w-3 h-3" /> Liked {pos > 0 ? `(${pos}%)` : ''}
                  </p>
                  {el.positive_reactions.length > 0 ? (
                    <ReactionQuote reaction={el.positive_reactions[0]} positive />
                  ) : (
                    <p className="text-xs text-[#9CA3AF] italic">No positive reactions</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <ThumbsDown className="w-3 h-3" /> Disliked {neg > 0 ? `(${neg}%)` : ''}
                  </p>
                  {el.negative_reactions.length > 0 ? (
                    <ReactionQuote reaction={el.negative_reactions[0]} positive={false} />
                  ) : (
                    <p className="text-xs text-[#9CA3AF] italic">No negative reactions</p>
                  )}
                </div>
              </div>

              {el.key_insight && (
                <p className="text-xs text-[#374151] mt-2 pt-2 border-t border-gray-100">
                  <span className="font-semibold text-[#07E5D1]">Insight:</span> {el.key_insight}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Expected-but-missing front-of-pack elements: things this category/audience
// expects at a glance (e.g. sugar content) that the pack omits. Sourced from
// structural evidence — category norms, competitors, and audience priorities —
// One labeled evidence row inside a gap card. The label names the KIND of proof
// (Audience / Competitors / Category) so the reader can weigh each signal.
const GapEvidence: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <div className="flex items-start gap-2 text-xs">
    <span className="flex items-center gap-1 text-amber-700 font-semibold shrink-0 w-[92px]">
      {icon}
      {label}
    </span>
    <span className="text-[#4B5563] flex-1">{children}</span>
  </div>
);

const CategoryGaps: React.FC<{ gaps: CategoryGap[] }> = ({ gaps }) => {
  if (!gaps.length) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <SearchX className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <h3 className="text-lg md:text-xl font-bold text-[#1C1C1E]">Expected but Missing</h3>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
          {gaps.length} gap{gaps.length > 1 ? 's' : ''}
        </span>
      </div>
      <p className="text-xs text-[#6B7280] mb-4 max-w-2xl">
        Elements this category and audience expect at a glance that aren&rsquo;t on the front of the
        pack. Each gap shows the evidence behind it.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {gaps.map((gap, idx) => (
          <div
            key={idx}
            className="relative rounded-xl border-2 border-dashed border-amber-300 bg-gradient-to-b from-amber-50/60 to-transparent p-4"
          >
            {/* Header: dashed-slot marker signals an empty spot on the pack */}
            <div className="flex items-center gap-2 mb-2">
              <CircleDashed className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <h4 className="font-semibold text-sm text-[#1C1C1E] flex-1">{gap.element}</h4>
              <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-700 border border-amber-300 rounded px-1.5 py-0.5 whitespace-nowrap">
                Not on front
              </span>
            </div>

            <p className="text-xs text-[#374151] mb-3">{gap.why_expected}</p>

            <div className="space-y-1.5 border-t border-amber-200/70 pt-2.5">
              {gap.audience_priority_signal ? (
                <GapEvidence icon={<Users className="w-3 h-3" />} label="Audience">
                  {gap.audience_priority_signal}
                </GapEvidence>
              ) : null}
              {gap.competitor_signal ? (
                <GapEvidence icon={<Store className="w-3 h-3" />} label="Competitors">
                  {gap.competitor_signal}
                </GapEvidence>
              ) : null}
              {gap.category_norm_basis ? (
                <GapEvidence icon={<Tag className="w-3 h-3" />} label="Category">
                  {gap.category_norm_basis}
                </GapEvidence>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Prioritized, evidence-grounded plan to raise the Front Performance Score.
// Each lever is tied to a real blocker / weak dimension / gap by the backend.
const ImprovementPlan: React.FC<{ plan: ImprovementPlanData; score: number }> = ({
  plan,
  score,
}) => {
  if (!plan.levers?.length) return null;

  return (
    <div>
      {/* Header matches the other Front Analysis subsections: icon + bold title + explainer */}
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5 text-[#07E5D1] flex-shrink-0" />
        <h3 className="text-lg md:text-xl font-bold text-[#1C1C1E]">How to Raise This Score</h3>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#0891b2] bg-[#07E5D1]/15 rounded-full px-2 py-0.5 whitespace-nowrap">
          from {Math.round(score)}/100
        </span>
      </div>
      <p className="text-xs text-[#6B7280] mb-4 max-w-2xl">
        {plan.current_state ||
          'Prioritized changes that would move the score, each tied to what is holding it back.'}
      </p>

      <div className="space-y-3">
        {plan.levers.map((lev, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#07E5D1] text-white text-[10px] font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <h4 className="font-semibold text-sm text-[#1C1C1E] flex-1">{lev.action}</h4>
              {lev.lever ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#6B7280]">
                  <Tag className="w-3 h-3" />
                  {lev.lever}
                </span>
              ) : null}
            </div>
            <p className="text-xs text-[#374151]">{lev.rationale}</p>
            {lev.expected_impact ? (
              <p className="text-[11px] text-[#6B7280] mt-2 pt-2 border-t border-gray-100">
                {lev.expected_impact}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const FrontAnalysisSection: React.FC<FrontAnalysisSectionProps> = ({
  data,
  frontImageUrl,
  buildImageUrl,
}) => {
  return (
    <div className="bg-white rounded-lg md:border md:border-gray-200 p-3 md:p-6">
      <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
        <Award className="w-5 h-5 text-[#07E5D1] mr-2" />
        <div>
          <h2 className="text-xl font-semibold text-[#1C1C1E]">Front Analysis</h2>
          <p className="text-xs text-[#6B7280] mt-1">
            Consumer reactions to the front view of the packaging
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Hero Score */}
        <div className="flex justify-center">
          <HeroScore score={data.overall_score} benchmark_will_buy={data.benchmark_will_buy} />
        </div>

        {/* Key elements header + explainer — always shown (independent of the heatmap, which
            only renders when the LLM returns bounding boxes). */}
        {data.elements.some(
          (el) => el.positive_reactions.length > 0 || el.negative_reactions.length > 0,
        ) && (
          <div>
            <h3 className="text-lg md:text-xl font-bold text-[#1C1C1E] mb-1 flex items-center justify-center gap-2 text-center">
              <Eye className="w-5 h-5 text-[#07E5D1] flex-shrink-0" />
              Key Elements Impacting Purchase Intent
            </h3>
            <p className="text-xs text-[#6B7280] mb-4 text-center max-w-xl mx-auto">
              Ranked by the share of shoppers who reacted to each element — the % combines those who
              called it out as a reason to buy and those who called it out as a reason not to. A
              higher % means the element shaped more shoppers&rsquo; purchase decision.
            </p>

            {/* Attention heatmap (only when the LLM returned bounding boxes) */}
            {frontImageUrl && data.bounding_boxes && data.bounding_boxes.length > 0 && (
              <div className="flex justify-center mb-8">
                <AttentionHeatmap
                  imageUrl={frontImageUrl}
                  boxes={data.bounding_boxes}
                  elements={data.elements}
                  buildImageUrl={buildImageUrl}
                />
              </div>
            )}

            {/* Unified ranked element list: Driver / Blocker / Mixed, with liked + disliked quotes */}
            <KeyElementsList elements={data.elements} />
          </div>
        )}

        {/* Expected-but-missing front-of-pack elements (independent of the element list) */}
        {data.category_gaps && data.category_gaps.length > 0 && (
          <CategoryGaps gaps={data.category_gaps} />
        )}

        {/* How to raise the score — prioritized, evidence-grounded plan */}
        {data.improvement_plan && data.improvement_plan.levers?.length > 0 && (
          <ImprovementPlan plan={data.improvement_plan} score={data.overall_score} />
        )}
      </div>
    </div>
  );
};

export default FrontAnalysisSection;
